import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { type FormEvent, useState } from "react";
import { jitUserProvisioningEnabled } from "../../../../shared/jitProvisioning";
import { sharedMutators } from "../../../../shared/mutators";
import { generateId } from "../../../../shared/id";
import { ssoIssuerUrlError } from "../../../../shared/ssoIssuerUrl";
import { Box } from "../../Box";
import { Button } from "../../Button";
import { PressButton } from "../../PressButton";
import { Text } from "../../Text";
import {
  DEFAULT_OIDC_SCOPES,
  emptySsoConfig,
  parseScopes,
  type Protocol,
} from "./constants";
import { newScimToken } from "./scimToken";
import { SsoProviderFormFields } from "./SsoProviderFormFields";
import { Field, FormActions, Section } from "./shared";

const ProtocolTabContainer = styled("div")`
  display: flex;
  align-items: center;
  align-self: flex-start;
  height: 32px;
  background: var(--alpha4);
  border-radius: 8px;
  padding: 0 2px;
  position: relative;
`;

const tabWidth = 52;

const ProtocolTabIndicator = styled("div")`
  position: absolute;
  width: ${tabWidth}px;
  height: 28px;
  border-radius: 6px;
  background: var(--surface-primary);
  box-shadow: var(--elevation-low);
  outline: 0.5px solid var(--border-alpha);
  transition: transform 0.15s ease;
  pointer-events: none;
`;

const ProtocolTab = styled(PressButton)`
  width: ${tabWidth}px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  padding: 0;
  background: transparent;
  font-family: inherit;
  font-weight: 450;
  font-size: 10px;
  line-height: 12px;
  color: var(--text-secondary);
  opacity: 0.5;
  position: relative;
  z-index: 1;
  &:hover {
    color: var(--text-primary);
    opacity: 1;
  }
  &[data-active="true"] {
    color: var(--text-primary);
    opacity: 1;
  }
`;

export function NewSsoProvider({
  onGeneratedScimToken,
}: {
  onGeneratedScimToken: (token: string | null) => void;
}) {
  const z = useZero();
  // OIDC providers share the stable "oidc" id so the redirect URI registered
  // at the IdP survives the provider being re-created. SAML keeps a generated
  // id until its IdP-initiated flow can resolve the provider without the ACS
  // path segment; it's minted once so switching protocol tabs never churns it.
  const [samlProviderId] = useState(() => generateId());
  const providerIdFor = (protocol: Protocol) =>
    protocol === "oidc" ? "oidc" : samlProviderId;
  const [config, setConfig] = useState(() => ({
    ...emptySsoConfig,
    providerId: providerIdFor(emptySsoConfig.protocol),
    scopes: DEFAULT_OIDC_SCOPES.join(" "),
  }));

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    // Mint the token before any mutation: the optimistic registerSSOProvider
    // insert flips the parent over to ExistingSsoProvider, so awaiting between
    // the two mutations would unmount this form mid-save and split them into
    // separate, non-atomic pushes.
    const scim = config.syncMode === "scim" ? await newScimToken() : null;

    const shared = {
      id: generateId(),
      providerId: config.providerId.trim(),
      issuer: config.issuer.trim(),
      domain: config.domain.trim().toLowerCase(),
      syncMode: config.syncMode,
    };
    const { image, ...requiredMapping } = config.mapping;
    const args =
      config.protocol === "oidc"
        ? {
            ...shared,
            protocol: "oidc" as const,
            clientId: config.clientId.trim(),
            clientSecret: config.clientSecret.trim(),
            scopes: parseScopes(config.scopes),
          }
        : {
            ...shared,
            protocol: "saml" as const,
            entryPoint: config.entryPoint.trim(),
            cert: config.cert.trim(),
            ...(config.metadataUrl.trim() && {
              metadataUrl: config.metadataUrl.trim(),
            }),
            ...(config.metadataXml.trim() && {
              metadataXml: config.metadataXml.trim(),
            }),
            ...(jitUserProvisioningEnabled({ syncMode: config.syncMode }) && {
              mapping: {
                email: requiredMapping.email.trim(),
                firstName: requiredMapping.firstName.trim(),
                lastName: requiredMapping.lastName.trim(),
                ...(image.trim() && { image: image.trim() }),
              },
            }),
          };
    z.mutate(sharedMutators.registerSSOProvider(args));

    if (scim) {
      z.mutate(
        sharedMutators.generateScimToken({
          ssoProviderId: shared.id,
          tokenHash: scim.hash,
        })
      );
      onGeneratedScimToken(scim.token);
    }

    setConfig({
      ...emptySsoConfig,
      providerId: providerIdFor(emptySsoConfig.protocol),
      scopes: DEFAULT_OIDC_SCOPES.join(" "),
    });
  }

  // With no SAML method chosen, the IdP fields aren't rendered, so native
  // `required` can't fire; gate the button until something is entered.
  const samlEmpty =
    !config.metadataUrl.trim() &&
    !config.metadataXml.trim() &&
    !config.issuer.trim() &&
    !config.entryPoint.trim() &&
    !config.cert.trim();
  const oidcIssuerInvalid =
    config.protocol === "oidc" &&
    !!config.issuer.trim() &&
    ssoIssuerUrlError(config.issuer.trim()) !== null;
  const disableSubmit =
    (config.protocol === "saml" && samlEmpty) || oidcIssuerInvalid;

  return (
    <form onSubmit={(e) => void handleSubmit(e)}>
      <Section>
        <Field>
          <Box horizontal={8} vertical={10}>
            <Text variant="text-small-md">Protocol</Text>
          </Box>
          <ProtocolTabContainer>
            <ProtocolTabIndicator
              style={{
                transform: `translateX(${config.protocol === "oidc" ? 0 : tabWidth}px)`,
              }}
            />
            {(
              [
                { value: "oidc", label: "OIDC" },
                { value: "saml", label: "SAML" },
              ] as const
            ).map((opt) => (
              <ProtocolTab
                key={opt.value}
                data-active={config.protocol === opt.value}
                onPress={() =>
                  setConfig((f) => ({
                    ...f,
                    protocol: opt.value,
                    providerId: providerIdFor(opt.value),
                  }))
                }
              >
                {opt.label}
              </ProtocolTab>
            ))}
          </ProtocolTabContainer>
        </Field>

        <SsoProviderFormFields config={config} onConfigChange={setConfig} />

        <FormActions>
          <Button
            variant="primary"
            size="l"
            type="submit"
            disabled={disableSubmit}
          >
            Add provider
          </Button>
        </FormActions>
      </Section>
    </form>
  );
}
